import * as Sentry from '@sentry/react';

const API_BASE = '/api';

/**
 * Sprint hotfix B-045 — telemetria sieci dla upload/download w terenie.
 *
 * Network Information API (`navigator.connection`) jest dostępne w Chrome/Edge/Android,
 * brak w Safari/iOS. Bez błędu — zwracamy 'unknown' / null gdy niedostępne.
 *
 * Używane jako Sentry breadcrumb context — pomocne przy diagnozie bugów typu
 * „upload się gubi w słabym 4G" (sprawdzasz w Sentry: ten user miał slow-2g
 * w momencie błędu).
 */
function getConnectionInfo(): {
  type: string;
  downlinkMbps: number | null;
  rttMs: number | null;
  saveData: boolean;
} {
  const conn = typeof navigator !== 'undefined' ? navigator.connection : undefined;
  return {
    type: conn?.effectiveType ?? 'unknown',
    downlinkMbps: conn?.downlink ?? null,
    rttMs: conn?.rtt ?? null,
    saveData: conn?.saveData ?? false,
  };
}

/**
 * Suma rozmiaru wszystkich File/Blob w FormData (w bajtach). Pomija text fields.
 */
function getFormDataSize(formData: FormData): number {
  let total = 0;
  for (const value of formData.values()) {
    if (value instanceof Blob) {
      total += value.size;
    }
  }
  return total;
}

const SLOW_UPLOAD_THRESHOLD_MS = 10_000;
const SLOW_DOWNLOAD_THRESHOLD_MS = 5_000;

function readStorage(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
}

function writeStorage(key: string, value: string, persist: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  // Clear opposing store to avoid ghost values from a prior session
  const primary = persist ? window.localStorage : window.sessionStorage;
  const opposite = persist ? window.sessionStorage : window.localStorage;
  primary.setItem(key, value);
  opposite.removeItem(key);
}

function clearStorage(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
}

function removeStoredUser() {
  clearStorage('user');
}

function decodeBase64(value: string): string {
  if (typeof atob === 'function') {
    return atob(value);
  }

  const buffer = (globalThis as { Buffer?: { from: (input: string, encoding: string) => { toString: (targetEncoding: string) => string } } }).Buffer;
  if (buffer) {
    return buffer.from(value, 'base64').toString('utf8');
  }

  throw new Error('Base64 decode is not available');
}

let accessToken: string | null = readStorage('accessToken');
let refreshToken: string | null = readStorage('refreshToken');
let refreshRequest: Promise<boolean> | null = null;

/**
 * Callback wywoływany po udanym odświeżeniu tokenu (np. auto-exit z impersonacji).
 * auth.tsx rejestruje handler żeby wyczyścić stan impersonacji gdy refresh
 * przywróci oryginalny token podczas aktywnej impersonacji.
 */
let onTokenRefreshedCallback: (() => void) | null = null;

export function registerOnTokenRefreshed(cb: () => void) {
  onTokenRefreshedCallback = cb;
}

export function unregisterOnTokenRefreshed() {
  onTokenRefreshedCallback = null;
}

export function setTokens(access: string, refresh: string, persist = true) {
  accessToken = access;
  refreshToken = refresh;
  writeStorage('accessToken', access, persist);
  writeStorage('refreshToken', refresh, persist);
}

/**
 * Ustaw tylko accessToken (bez nadpisywania refreshToken).
 * Używane podczas impersonacji — oryginalny refresh pozostaje nienaruszony.
 */
export function setAccessTokenOnly(access: string) {
  accessToken = access;
  // Determine persist mode from current refresh token storage location
  const persist = typeof window !== 'undefined' && !!window.localStorage.getItem('refreshToken');
  writeStorage('accessToken', access, persist);
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  clearStorage('accessToken');
  clearStorage('refreshToken');
}

export function getAccessToken() {
  return accessToken;
}

export function isAccessTokenValid(token = accessToken): boolean {
  if (!token) {
    return false;
  }

  try {
    const payload = JSON.parse(decodeBase64(token.split('.')[1] ?? ''));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function getErrorMessage(data: unknown, fallback: string) {
  if (
    data &&
    typeof data === 'object' &&
    'message' in data &&
    typeof data.message === 'string'
  ) {
    return data.message;
  }

  return fallback;
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshToken) return false;

  if (!refreshRequest) {
    refreshRequest = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!res.ok) {
          clearTokens();
          return false;
        }

        const data = await res.json();
        setTokens(data.accessToken, data.refreshToken);
        // Powiadom auth.tsx o odświeżeniu — pozwala wyczyścić stan impersonacji
        // gdy oryginalna sesja zostanie przywrócona podczas aktywnej impersonacji.
        onTokenRefreshedCallback?.();
        return true;
      } catch {
        return false;
      } finally {
        refreshRequest = null;
      }
    })();
  }

  return refreshRequest;
}

export async function restoreSession(): Promise<boolean> {
  if (isAccessTokenValid()) {
    return true;
  }

  if (!refreshToken) {
    return false;
  }

  const restored = await refreshAccessToken();
  if (!restored) {
    clearTokens();
    removeStoredUser();
  }

  return restored;
}

function handleUnauthorized(): never {
  clearTokens();
  removeStoredUser();

  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }

  // `status` — żeby wołający z ponawianiem (miniatury symboli,
  // `symbolSvgSource.ts`) nie brali wygasłej sesji za błąd sieci.
  throw Object.assign(new Error('Sesja wygasła'), { status: 401 });
}

async function requestWithAuth(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    }
  }

  if (res.status === 401) {
    handleUnauthorized();
  }

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const err = new Error(getErrorMessage(data, `Błąd ${res.status}`)) as Error & {
      status?: number;
      errors?: string[];
    };
    err.status = res.status;
    // Jak w api(): lista błędów walidacji (np. struktura biblioteki symboli)
    // musi dotrzeć do UI, nie tylko zbiorczy komunikat.
    if (data && Array.isArray(data.errors)) err.errors = data.errors;
    throw err;
  }

  return res;
}

export async function api<T = any>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Try refresh on 401
  if (res.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    }
  }

  if (res.status === 401) {
    handleUnauthorized();
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || `Błąd ${res.status}`) as Error & {
      status?: number;
      errors?: string[];
    };
    err.status = res.status;
    if (Array.isArray(data.errors)) err.errors = data.errors;
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Upload helper z telemetrią Sentry (B-045).
 *
 * Każdy upload generuje breadcrumb z metadata: rozmiar pliku, czas trwania,
 * typ połączenia (4g/3g/slow-2g), downlink Mbps, status. Wolne uploady
 * (>10s) trafiają jako `level: warning` — w Sentry łatwo filtrować.
 *
 * Po tygodniu używania w terenie wiesz konkretnie: które uploady zwalniają,
 * w jakiej sieci, z jakimi rozmiarami plików. Bez instrumentacji to było
 * subiektywne („wydaje mi się że tu się tnie").
 */
export async function upload(
  path: string,
  formData: FormData,
  options: RequestInit = {},
): Promise<Response> {
  const start = performance.now();
  const fileSize = getFormDataSize(formData);
  const conn = getConnectionInfo();

  try {
    const response = await requestWithAuth(path, {
      ...options,
      method: options.method ?? 'POST',
      body: formData,
    });

    const durationMs = Math.round(performance.now() - start);
    const isSlow = durationMs > SLOW_UPLOAD_THRESHOLD_MS;

    Sentry.addBreadcrumb({
      category: 'upload',
      level: isSlow ? 'warning' : 'info',
      message: `Upload ${path} ${response.ok ? 'OK' : 'FAIL'} ${durationMs}ms`,
      data: {
        path,
        file_size_bytes: fileSize,
        file_size_mb: +(fileSize / 1024 / 1024).toFixed(2),
        duration_ms: durationMs,
        duration_s: +(durationMs / 1000).toFixed(1),
        throughput_mbps: fileSize > 0 && durationMs > 0
          ? +((fileSize * 8) / 1e6 / (durationMs / 1000)).toFixed(2)
          : null,
        connection_type: conn.type,
        connection_downlink_mbps: conn.downlinkMbps,
        connection_rtt_ms: conn.rttMs,
        save_data: conn.saveData,
        status: response.status,
        ok: response.ok,
      },
    });

    return response;
  } catch (err) {
    const durationMs = Math.round(performance.now() - start);
    Sentry.addBreadcrumb({
      category: 'upload',
      level: 'error',
      message: `Upload ${path} ERROR ${durationMs}ms`,
      data: {
        path,
        file_size_bytes: fileSize,
        file_size_mb: +(fileSize / 1024 / 1024).toFixed(2),
        duration_ms: durationMs,
        connection_type: conn.type,
        connection_downlink_mbps: conn.downlinkMbps,
        save_data: conn.saveData,
        error: err instanceof Error ? err.message : String(err),
      },
    });
    throw err;
  }
}

/**
 * Fetch z auth + telemetria pobierania (B-045). Symetryczna do `upload()` —
 * w terenie download też potrafi boleć (np. galeria w słabym 4G,
 * pobieranie dokumentu inspekcji 50MB).
 */
export async function fetchBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const start = performance.now();
  const conn = getConnectionInfo();

  try {
    const res = await requestWithAuth(path, options);
    const blob = await res.blob();
    const durationMs = Math.round(performance.now() - start);
    const isSlow = durationMs > SLOW_DOWNLOAD_THRESHOLD_MS;

    Sentry.addBreadcrumb({
      category: 'download',
      level: isSlow ? 'warning' : 'info',
      message: `Download ${path} ${blob.size}B ${durationMs}ms`,
      data: {
        path,
        size_bytes: blob.size,
        size_mb: +(blob.size / 1024 / 1024).toFixed(2),
        duration_ms: durationMs,
        connection_type: conn.type,
        connection_downlink_mbps: conn.downlinkMbps,
        status: res.status,
      },
    });

    return blob;
  } catch (err) {
    const durationMs = Math.round(performance.now() - start);
    Sentry.addBreadcrumb({
      category: 'download',
      level: 'error',
      message: `Download ${path} ERROR ${durationMs}ms`,
      data: {
        path,
        duration_ms: durationMs,
        connection_type: conn.type,
        error: err instanceof Error ? err.message : String(err),
      },
    });
    throw err;
  }
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const blob = await fetchBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Nazwa pliku z nagłówka `Content-Disposition` (RFC 6266) — preferuje wariant
 * `filename*=UTF-8''...` (RFC 5987, obsługuje znaki spoza ASCII), w drugiej
 * kolejności `filename="..."`/`filename=...`. Brak nagłówka albo brak w nim
 * nazwy pliku → `fallback`.
 */
export function filenameFromContentDisposition(
  headerValue: string | null | undefined,
  fallback: string,
): string {
  if (!headerValue) return fallback;

  const utf8Match = headerValue.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      // Malformed percent-encoding — spadnij do ASCII wariantu poniżej.
    }
  }

  const asciiMatch = headerValue.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (asciiMatch?.[1]) return asciiMatch[1].trim();

  return fallback;
}

/**
 * Jak `downloadFile`, ale zwraca nagłówki odpowiedzi (np. `X-Symbol-Export-Skipped*`
 * przy eksporcie biblioteki symboli) — `downloadFile`/`fetchBlob` ich nie
 * eksponują. Nazwę pliku bierze z `Content-Disposition`, gdy serwer go ustawił
 * (patrz `filenameFromContentDisposition`), inaczej z `fallbackFilename`.
 */
export async function downloadFileWithHeaders(
  path: string,
  fallbackFilename: string,
): Promise<Headers> {
  const res = await requestWithAuth(path);
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(
    res.headers.get('Content-Disposition'),
    fallbackFilename,
  );

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);

  return res.headers;
}


export function downloadText(content: string, filename: string, mime = 'text/plain'): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function loadAuthenticatedImage(path: string): Promise<HTMLImageElement> {
  const blob = await fetchBlob(path);
  const objectUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      // Sprint 58 (B-061 hotfix #konva) — guard przeciw 0×0 image. Może się
      // zdarzyć przy korupcji blob'a / pustym body. Konva.drawImage rzuca
      // wtedy InvalidStateError. Traktujemy jako load error.
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        reject(new Error('Obraz ma zerowe wymiary'));
        return;
      }
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Nie udało się wczytać obrazu'));
    };
    img.src = objectUrl;
  });
}
